import { NgClass } from '@angular/common';
import { AfterViewInit, Component, computed, ElementRef, input, OnDestroy, signal, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { camelCase } from 'lodash';
import { TStatusBadge, TStatusBadgeColorScheme, TStatusBadgeColorType } from './status-badge.interface';

@Component({
  imports: [TranslateModule, NgClass],
  selector: 'app-status-badge',
  styleUrl: './status-badge.component.scss',
  templateUrl: './status-badge.component.html',
})
export class StatusBadgeComponent implements AfterViewInit, OnDestroy {
  public status = input.required<TStatusBadge>();

  public colorScheme = input<TStatusBadgeColorScheme>('campaign');
  public colorType = input<TStatusBadgeColorType>('');
  public size = input<'micro' | 'small' | 'medium' | 'large'>('large');
  public hasBackground = input<boolean>(true);
  public hasBorder = input<boolean>(false);
  public detectCurrentColors = input<boolean>(false);

  public statusLowerCase = computed(() => camelCase(this.status()));
  public hasHorizontalPadding = computed(() => this.hasBackground() || this.hasBorder());

  private readonly defaultBackgroundProbe = viewChild.required<ElementRef<HTMLSpanElement>>('defaultBackgroundProbe');

  private readonly backgroundColorSignal = signal('transparent');
  private readonly textColorSignal = signal('transparent');

  public readonly currentBackgroundColor = this.backgroundColorSignal.asReadonly();
  public readonly currentTextColor = this.textColorSignal.asReadonly();

  private observer: IntersectionObserver | null = null;

  public ngAfterViewInit(): void {
    if (!this.detectCurrentColors()) {
      return;
    }

    const { nativeElement: probeElement } = this.defaultBackgroundProbe();

    this.observer = new IntersectionObserver((entries) => {
      if (entries.length === 0 || !entries[0].isIntersecting || typeof window === 'undefined') {
        return;
      }

      const style = window.getComputedStyle(probeElement);
      const normalizeColor = (c: string): string =>
        !c || c === 'rgba(0, 0, 0, 0)' || c === 'transparent' ? 'transparent' : c;

      this.backgroundColorSignal.set(normalizeColor(style.backgroundColor));
      this.textColorSignal.set(normalizeColor(style.color));

      this.observer?.disconnect();
      this.observer = null;
    });

    this.observer.observe(probeElement);
  }

  public ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
  }
}
